import { SparklesIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef } from 'react';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { type TemplateRecommendation, useTemplateTranslation } from './kit';

/** Plays a muted preview only while it's on screen. */
function PreviewVideo({ src, poster }: { src: string; poster?: string }) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = ref.current;
    if (!video || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      },
      { threshold: 0.3 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      muted
      loop
      playsInline
      preload='none'
      className='absolute inset-0 h-full w-full object-cover'
    />
  );
}

/** Curated tools that continue the job (turn the names into portraits…). */
export default function TemplateNextSteps({
  recommendations,
}: {
  recommendations: TemplateRecommendation[];
}) {
  const { t } = useTemplateTranslation();
  const { t: tr } = useLocale('recommendations');
  if (!recommendations.length) return null;

  const titleOf = (item: TemplateRecommendation) =>
    item.titleNs === 'tools' ? t(item.title) : tr(item.title);

  return (
    <section className='mx-auto max-w-6xl px-4 pt-24 sm:px-6 sm:pt-32 lg:px-8'>
      <SectionHeading
        size='md'
        title={t('Take it further.')}
        tail={t('Pair it with these tools.')}
      />
      <ul className='mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
        {recommendations.map((item, index) => {
          const title = titleOf(item);
          const media = item.image || item.video;
          return (
            <li key={`${index}-${item.slug}`}>
              <Link
                href={item.href}
                // A new tab keeps whatever the visitor just generated here.
                target='_blank'
                className={clsxm(
                  'group relative flex h-full flex-col overflow-hidden rounded-[22px] transition duration-200 hover:ring-[#0f0f28]/[0.16] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:ring-white/20',
                  tone.surface,
                  tone.line,
                )}
              >
                {media ? (
                  <span className='relative block aspect-[16/10] w-full overflow-hidden bg-[#eeeefc] dark:bg-[#15142a]'>
                    {item.video ? (
                      <PreviewVideo src={item.video} poster={item.image} />
                    ) : (
                      <Image
                        src={item.image!}
                        alt=''
                        fill
                        sizes='(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'
                        className='object-cover'
                      />
                    )}
                  </span>
                ) : null}
                <span className='flex flex-1 flex-col p-6'>
                  {!media ? (
                    <span
                      className={clsxm(
                        'mb-5 flex h-11 w-11 items-center justify-center rounded-xl dark:bg-white/[0.06] dark:text-primary-300',
                        item.iconBackground || 'bg-primary-50',
                        item.iconForeground || 'text-primary-600',
                      )}
                    >
                      <SparklesIcon className='h-5 w-5' aria-hidden='true' />
                    </span>
                  ) : null}
                  <span
                    className={clsxm(
                      'text-[17px] font-semibold leading-6',
                      tone.ink,
                    )}
                  >
                    {title}
                  </span>
                  <span
                    className={clsxm('mt-2 text-[15px] leading-6', tone.body)}
                  >
                    {tr(item.message)}
                  </span>
                  <span className='mt-5 inline-flex items-center gap-1.5 text-[14px] font-semibold text-primary-700 dark:text-primary-300'>
                    {tr('Try it now')}
                    <ArrowGlyph />
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
